// TargetsProvider.ts
import React, { useState, useCallback, useRef, ReactNode, FC } from 'react';
import { TargetsContext, Target } from './TargetsContext';
import { route, GoalType } from '../utils/apiRoutes';
import { sortTargets } from '../utils/sortUtils';
import { transformTargetFromList, transformTargetPreview } from '../utils/transformApiResponse';

const TargetsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [targets, setTargets] = useState<Target[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const fetchedGoalIdsRef = useRef<Set<string>>(new Set());
  const currentCacheKeyRef = useRef<string | null>(null);
  const activePromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  const completedFetchesRef = useRef<Set<string>>(new Set());
  
  const fetchTargetsByGoal = useCallback(async (goalId: number, goalType: GoalType) => {
    const cacheKey = `${goalId}-${goalType}`;
    
    // CRITICAL: If we've already successfully fetched this, return a resolved promise
    if (completedFetchesRef.current.has(cacheKey)) {
      console.log(`[TargetsProvider] Already completed fetch for ${cacheKey}, skipping`);
      return Promise.resolve();
    }
    
    // CRITICAL: If we're already fetching this exact combination, return the existing promise
    const existingPromise = activePromisesRef.current.get(cacheKey);
    if (existingPromise) {
      console.log(`[TargetsProvider] Already fetching ${cacheKey}, returning existing promise`);
      return existingPromise;
    }
    
    // Create the fetch promise
    const fetchPromise = (async () => {
      // Clear targets only when switching to a different goal/type combination
      if (currentCacheKeyRef.current !== cacheKey) {
        setTargets([]);
        currentCacheKeyRef.current = cacheKey;
      }
      
      setLoading(true);
      
      // Mark as fetched (will be removed on error)
      fetchedGoalIdsRef.current.add(cacheKey);
      
      try {
        // Use batch preview endpoint with goal_id filter - single call instead of N+1 calls
        const batchUrl = route.targetsPreviewBatch(goalType, goalId, false);
        const batchResponse = await fetch(batchUrl);
        
        if (!batchResponse.ok) {
          throw new Error(`Failed to fetch targets batch: ${batchResponse.status}`);
        }
        
        const batchData = await batchResponse.json();
        const targetsArray = batchData.targets || [];
        
        if (!Array.isArray(targetsArray) || targetsArray.length === 0) {
          setTargets([]);
          setLoading(false);
          activePromisesRef.current.delete(cacheKey);
          return;
        }
        
        // Transform batch response to Target format
        const transformedTargets = targetsArray.map((preview: any) => transformTargetPreview(preview));
        const validTargets = transformedTargets.filter((t): t is Target => t !== null);
        const sortedTargets = sortTargets(validTargets) as Target[];
        
        setTargets(sortedTargets);
        // Mark as completed AFTER successfully setting targets
        completedFetchesRef.current.add(cacheKey);
      } catch (error) {
        console.error('Error fetching targets for goal:', error);
        setTargets([]);
        // Remove from ref on error so it can be retried
        fetchedGoalIdsRef.current.delete(cacheKey);
        currentCacheKeyRef.current = null;
        // Don't mark as completed on error - allow retry
        throw error; // Re-throw so caller knows it failed
      } finally {
        setLoading(false);
        // Remove promise from map when done (success or error)
        // But keep it in completedFetchesRef if successful
        activePromisesRef.current.delete(cacheKey);
      }
    })();
    
    // Store the promise so concurrent calls can reuse it
    activePromisesRef.current.set(cacheKey, fetchPromise);
    
    return fetchPromise;
  }, []); // No dependencies - use ref for tracking

  // Fetch all targets for a goalType using batch preview endpoint (optimized - single API call)
  const fetchAllTargetsByType = useCallback(async (goalType: GoalType) => {
    setLoading(true);
    try {
      // Use batch preview endpoint - single call instead of N+1 calls
      const batchUrl = route.targetsPreviewBatch(goalType, undefined, false);
      const batchResponse = await fetch(batchUrl);
      
      if (!batchResponse.ok) {
        throw new Error(`Failed to fetch targets batch: ${batchResponse.status}`);
      }
      
      const batchData = await batchResponse.json();
      const targetsArray = batchData.targets || [];
      
      if (!Array.isArray(targetsArray)) {
        setTargets([]);
        return;
      }
      
      // Transform batch response to Target format
      const transformedTargets = targetsArray.map((preview: any) => transformTargetPreview(preview));
      const validTargets = transformedTargets.filter((t): t is Target => t !== null);
      const sorted = sortTargets(validTargets) as Target[];
      setTargets(sorted);
      fetchedGoalIdsRef.current.clear();
    } catch (error) {
      console.error('Error fetching all targets by type:', error);
      setTargets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCache = useCallback(() => {
    fetchedGoalIdsRef.current.clear();
  }, []);

  // Fetch a single target using preview endpoint
  const fetchSingleTarget = useCallback(async (targetId: number, goalType: GoalType): Promise<Target | null> => {
    setLoading(true);
    try {
      const response = await fetch(route.targetPreview(targetId, false));
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const preview = await response.json();
      return transformTargetPreview(preview);
    } catch (error) {
      console.error('Error fetching single target:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return React.createElement(
    TargetsContext.Provider,
    {
      value: {
        targets,
        loading,
        fetchTargetsByGoal,
        fetchAllTargetsByType,
        fetchSingleTarget,
        clearCache
      },
    },
    children
  );
};

export default TargetsProvider;
